import React from 'react';
import { Container } from '@mui/material';

import { CustomerRegistrationFormProps } from '../../types/company.types';
import { useCustomerRegistrationForm } from '../../hooks/customer/use-registration-form';
import { CurrentAddressSection } from '../current-address-section';
import { HeadOfficeAddressSection } from '../head-office-address-section';
import { RegisterForm } from '../register-form';
import { useStaffs, useDepartments } from '../../hooks';

import { CompanyInfoSection } from './info-section';

import type { CustomerRegistrationForm as CustomerRegistrationFormData } from '../../types/company.types';

import HeaderRegister from '@/features/apps/components/header-register';
import { FunctionName, PermissionAction } from '@/lib/enums';

export const CustomerRegistrationForm: React.FC<CustomerRegistrationFormProps> = ({
  onSubmit,
  onCancel,
}) => {
  const {
    formData,
    validationState,
    updateField,
    updateAddressField,
    copyCurrentToHeadOffice,
    addRepresentative,
    removeRepresentative,
    updateRepresentative,
    handleSubmit,
    getFieldError,
    isFieldValid,
    canAddRepresentative,
    isSubmitDisabled,
  } = useCustomerRegistrationForm(onSubmit);

  const handleFormSubmit = async () => {
    await handleSubmit();
  };

  const handleCancel = () => {
    onCancel();
  };

  // Global state hooks
  const { departments, loading: departmentsLoading } = useDepartments(PermissionAction.CREATE);
  const {
    loading: staffsLoading,
    loadStaffsByDepartment,
    getStaffsByDepartment,
    clearStaffsByDepartment,
  } = useStaffs();

  const getSalesPersonOptions = (departmentId: string) => {
    // Get staffs for specific department
    return getStaffsByDepartment(departmentId);
  };
  // Function to load staffs when department changes
  const handleDepartmentChange = async (departmentId: string) => {
    if (departmentId) {
      try {
        await loadStaffsByDepartment('sale', departmentId, FunctionName.COMPANY_MANAGEMENT);
      } catch (error) {
        console.error('Error loading staffs:', error);
      }
    } else {
      // Clear staffs if no department selected
      clearStaffsByDepartment();
    }
  };

  return (
    <Container maxWidth='lg' sx={{ py: 4 }}>
      {/* Header */}
      <HeaderRegister title='得意先登録フォーム' description='得意先情報を入力してください' />

      <RegisterForm
        validationState={validationState}
        formData={formData}
        addRepresentative={addRepresentative}
        removeRepresentative={removeRepresentative}
        updateRepresentative={updateRepresentative}
        getSalesPersonOptions={getSalesPersonOptions}
        getFieldError={getFieldError}
        isFieldValid={isFieldValid}
        canAddRepresentative={canAddRepresentative}
        departments={departments}
        departmentsLoading={departmentsLoading}
        staffsLoading={staffsLoading}
        handleDepartmentChange={(departmentId) => {
          void handleDepartmentChange(departmentId);
        }}
        handleCancel={handleCancel}
        handleFormSubmit={() => {
          void handleFormSubmit();
        }}
        isSubmitDisabled={isSubmitDisabled}
        hasNavigationPanel={false}
      >
        {/* Company Information Section */}
        <CompanyInfoSection
          formData={formData}
          onUpdateField={
            updateField as <K extends keyof CustomerRegistrationFormData>(
              field: K,
              value: CustomerRegistrationFormData[K]
            ) => void
          }
          getFieldError={getFieldError}
          isFieldValid={isFieldValid}
        />

        {/* Current Address Section */}
        <CurrentAddressSection
          currentAddress={formData.currentAddress}
          onUpdateAddressField={(field, value) =>
            updateAddressField('currentAddress', field, value)
          }
          getFieldError={getFieldError}
          isFieldValid={isFieldValid}
        />

        {/* Head Office Address Section */}
        <HeadOfficeAddressSection
          headOfficeAddress={formData.headOfficeAddress}
          onUpdateAddressField={(field, value) =>
            updateAddressField('headOfficeAddress', field, value)
          }
          onCopyCurrentToHeadOffice={copyCurrentToHeadOffice}
          getFieldError={getFieldError}
          isFieldValid={isFieldValid}
        />

        {/* Representatives Section */}
        {/* <RepresentativesSection
          representatives={formData.representatives}
          onAddRepresentative={addRepresentative}
          onRemoveRepresentative={removeRepresentative}
          onUpdateRepresentative={updateRepresentative}
          getSalesPersonOptions={getSalesPersonOptions}
          getFieldError={getFieldError}
          isFieldValid={isFieldValid}
          canAddRepresentative={canAddRepresentative}
        /> */}
      </RegisterForm>
    </Container>
  );
};
